import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpDown, Plus, X, ArrowUp, ArrowDown } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type SortField =
  | "score"
  | "capacity"
  | "size"
  | "credits"
  | "monthly_price"
  | "title"
  | "space_name"
  | "address"
  | "updated_at"
  | "status";

export type SortDirection = "asc" | "desc";

export interface SortRule {
  field: SortField;
  direction: SortDirection;
}

interface SortControlsProps {
  onSortChange: (sortRules: SortRule[]) => void;
  defaultSort?: SortRule[];
}

const SORT_FIELD_OPTIONS: { value: SortField; label: string }[] = [
  { value: "score", label: "Score" },
  { value: "capacity", label: "Capacidade" },
  { value: "size", label: "Tamanho" },
  { value: "credits", label: "Créditos" },
  { value: "monthly_price", label: "Preço mensal" },
  { value: "title", label: "Título" },
  { value: "space_name", label: "Espaço" },
  { value: "address", label: "Endereço" },
  { value: "updated_at", label: "Atualizado em" },
  { value: "status", label: "Status" },
];

function getDefaultDirectionForField(field: SortField): SortDirection {
  switch (field) {
    case "score":
      return "desc";
    case "capacity":
      return "desc";
    case "size":
      return "desc";
    case "credits":
      return "asc";
    case "monthly_price":
      return "asc";
    case "title":
      return "asc";
    case "space_name":
      return "asc";
    case "address":
      return "asc";
    case "updated_at":
      return "desc";
    case "status":
      return "asc";
    default:
      return "asc";
  }
}

function getSortDirectionTooltip(
  field: SortField,
  direction: SortDirection,
): string {
  const fieldLabel =
    SORT_FIELD_OPTIONS.find((opt) => opt.value === field)?.label || field;

  switch (field) {
    case "score":
      return direction === "desc"
        ? `${fieldLabel}: Maior para menor (melhores scores primeiro)`
        : `${fieldLabel}: Menor para maior`;
    case "capacity":
      return direction === "desc"
        ? `${fieldLabel}: Maior para menor (mais capacidade primeiro)`
        : `${fieldLabel}: Menor para maior`;
    case "size":
      return direction === "desc"
        ? `${fieldLabel}: Maior para menor (maior tamanho primeiro)`
        : `${fieldLabel}: Menor para maior`;
    case "credits":
      return direction === "asc"
        ? `${fieldLabel}: Menor para maior (menor preço primeiro)`
        : `${fieldLabel}: Maior para menor`;
    case "monthly_price":
      return direction === "asc"
        ? `${fieldLabel}: Menor para maior (menor preço primeiro)`
        : `${fieldLabel}: Maior para menor`;
    case "title":
      return direction === "asc"
        ? `${fieldLabel}: A a Z (alfabético)`
        : `${fieldLabel}: Z a A (alfabético reverso)`;
    case "space_name":
      return direction === "asc"
        ? `${fieldLabel}: A a Z (alfabético)`
        : `${fieldLabel}: Z a A (alfabético reverso)`;
    case "address":
      return direction === "asc"
        ? `${fieldLabel}: A a Z (alfabético)`
        : `${fieldLabel}: Z a A (alfabético reverso)`;
    case "updated_at":
      return direction === "desc"
        ? `${fieldLabel}: Mais recente primeiro`
        : `${fieldLabel}: Mais antigo primeiro`;
    case "status":
      return direction === "asc"
        ? `${fieldLabel}: Menor para maior (ordem numérica)`
        : `${fieldLabel}: Maior para menor`;
    default:
      return direction === "asc" ? "Crescente" : "Decrescente";
  }
}

export function SortControls({
  onSortChange,
  defaultSort = [{ field: "score", direction: "desc" }],
}: SortControlsProps) {
  const [sortRules, setSortRules] = useState<SortRule[]>(defaultSort);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (defaultSort && defaultSort.length > 0) {
      setSortRules(defaultSort);
    }
  }, [defaultSort]);

  const handleAddSort = () => {
    const defaultField: SortField = "score";
    const newSortRule: SortRule = {
      field: defaultField,
      direction: getDefaultDirectionForField(defaultField),
    };
    const updatedSortRules = [...sortRules, newSortRule];
    setSortRules(updatedSortRules);
    onSortChange(updatedSortRules);
  };

  const handleRemoveSort = (ruleIndex: number) => {
    const updatedSortRules = sortRules.filter(
      (_, index) => index !== ruleIndex,
    );
    setSortRules(updatedSortRules);
    onSortChange(updatedSortRules);
  };

  const handleFieldChange = (ruleIndex: number, newField: SortField) => {
    const defaultDirection = getDefaultDirectionForField(newField);
    const updatedSortRules = sortRules.map((sortRule, index) =>
      index === ruleIndex
        ? { ...sortRule, field: newField, direction: defaultDirection }
        : sortRule,
    );
    setSortRules(updatedSortRules);
    onSortChange(updatedSortRules);
  };

  const toggleDirection = (ruleIndex: number) => {
    const updatedSortRules = sortRules.map((sortRule, index) =>
      index === ruleIndex
        ? {
            ...sortRule,
            direction:
              sortRule.direction === "asc"
                ? ("desc" as SortDirection)
                : ("asc" as SortDirection),
          }
        : sortRule,
    );
    setSortRules(updatedSortRules);
    onSortChange(updatedSortRules);
  };

  const getFieldLabel = (field: SortField) => {
    return (
      SORT_FIELD_OPTIONS.find((opt) => opt.value === field)?.label || field
    );
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2"
          aria-label="Ordenar resultados"
        >
          <ArrowUpDown className="h-4 w-4" />
          <span className="hidden sm:inline">Ordenar</span>
          {sortRules.length > 0 && (
            <span className="hidden sm:inline text-xs text-muted-foreground">
              ({sortRules.length})
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Ordenação</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAddSort}
              className="h-7 gap-1"
            >
              <Plus className="h-3 w-3" />
              Adicionar
            </Button>
          </div>

          {sortRules.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-4">
              Nenhuma ordenação aplicada
            </div>
          ) : (
            <div className="space-y-2">
              {sortRules.map((sortRule, ruleIndex) => (
                <div
                  key={ruleIndex}
                  className="flex items-center gap-2 p-2 border rounded-md"
                >
                  <div className="flex-1 flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-6">
                      {ruleIndex + 1}
                    </span>
                    <Select
                      value={sortRule.field}
                      onValueChange={(value) =>
                        handleFieldChange(ruleIndex, value as SortField)
                      }
                    >
                      <SelectTrigger className="h-8 flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SORT_FIELD_OPTIONS.map((fieldOption) => (
                          <SelectItem
                            key={fieldOption.value}
                            value={fieldOption.value}
                          >
                            {fieldOption.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleDirection(ruleIndex)}
                            className="h-8 w-8 p-0"
                            aria-label={`Alternar direção para ${getFieldLabel(sortRule.field)}`}
                          >
                            {sortRule.direction === "desc" ? (
                              <ArrowDown className="h-4 w-4" />
                            ) : (
                              <ArrowUp className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="text-sm">
                            {getSortDirectionTooltip(
                              sortRule.field,
                              sortRule.direction,
                            )}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {sortRules.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSort(ruleIndex)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        aria-label="Remover ordenação"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
